import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import BrandLogosStrip from '@/pages/home/components/BrandLogosStrip';

function useVisible(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

interface CaseStudyData {
  id: string;
  propertyCode: string;
  fullName: string;
  location: string;
  brand: string;
  brandFamily: string;
  image: string;
  tag: string;
  tagline: string;
  awards: string[];
  status: string;
  statusColor: string;
  heroStats: { label: string; value: string; sub?: string; color: string }[];
  sections: {
    title: string;
    icon: string;
    iconBg: string;
    iconColor: string;
    items: { label?: string; value?: string; text?: string }[];
    type: 'bullets' | 'table' | 'progression';
  }[];
  closingNote: string;
}

const caseStudies: CaseStudyData[] = [
  {
    id: 'coifl',
    propertyCode: 'COIFL',
    fullName: 'Holiday Inn Express- Cocoa Beach',
    location: 'Florida',
    brand: 'IHG',
    brandFamily: 'IHG Hotels & Resorts',
    image: 'https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/bea685c3-7a0b-42ea-ac5b-e25ab8da6f55_Screenshot-2026-04-13-at-5.54.49PM.png?v=726e3ee0368b83d286eeadc95ace08c3',
    tag: 'IHG Target Triumph 2024 & 2025',
    tagline: 'Best-in-class execution across guest satisfaction, brand compliance, and operational performance.',
    awards: ['IHG Target Triumph Award – 2024', 'IHG Target Triumph Award – 2025'],
    status: 'Elite (Best-in-Class)',
    statusColor: 'text-emerald-600 bg-emerald-50',
    heroStats: [
      { label: '2026 YTD Score', value: '91.25', sub: '2026 YTD', color: 'text-amber-600' },
      { label: '2025 Score', value: '87.67', sub: '2025', color: 'text-slate-300' },
      { label: 'Current Status', value: 'Elite', sub: 'IHG Tier', color: 'text-emerald-400' },
      { label: 'Awards Won', value: '2×', sub: 'Target Triumph', color: 'text-amber-600' },
    ],
    sections: [
      {
        title: 'Key Achievements',
        icon: 'ri-trophy-line',
        iconBg: 'bg-amber-50',
        iconColor: 'text-amber-600',
        type: 'bullets',
        items: [
          { text: 'IHG Target Triumph Award – 2024' },
          { text: 'IHG Target Triumph Award – 2025' },
          { text: 'Full Renovation Completed – October 2024' },
          { text: 'Current Status – Elite (Best-in-Class)' },
        ],
      },
      {
        title: 'Guest Love Score Progression',
        icon: 'ri-heart-line',
        iconBg: 'bg-rose-50',
        iconColor: 'text-rose-500',
        type: 'table',
        items: [
          { label: '2024', value: '80.37 — Reputable' },
          { label: '2025', value: '87.67 — Superior' },
          { label: '2026 (YTD)', value: '91.25 — Elite' },
        ],
      },
      {
        title: 'Performance Growth',
        icon: 'ri-line-chart-line',
        iconBg: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        type: 'progression',
        items: [
          { label: '2024', value: '80.37' },
          { label: '2025', value: '87.67' },
          { label: '2026 YTD', value: '91.25' },
        ],
      },
    ],
    closingNote: 'Through disciplined execution, adherence to brand standards, and a strong service culture, COIFL continues to outperform rising IHG benchmarks and maintain its position among top-tier assets.',
  },
  {
    id: 'coiaa',
    propertyCode: 'COIAA',
    fullName: 'Holiday Inn Express- Cocoa',
    location: 'FLORIDA',
    brand: 'IHG',
    brandFamily: 'IHG Hotels & Resorts',
    image: 'https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/97c9b568-7e2a-4b4a-96b2-3e758d4908d7_2024-07-17.webp?v=275b997d2a719267af7b7d16f1e33a40',
    tag: 'IHG Target Triumph – Renovation Period',
    tagline: 'Market-leading RevPAR and ADR performance maintained throughout an active 8-month renovation.',
    awards: ['IHG Target Triumph Award – Renovation Period'],
    status: 'Market Leader',
    statusColor: 'text-amber-700 bg-amber-50',
    heroStats: [
      { label: 'Guest Experience', value: '86.67', sub: '8-Month Avg', color: 'text-amber-600' },
      { label: 'Room Cleanliness', value: '97.28', sub: '8-Month Avg', color: 'text-emerald-400' },
      { label: 'ADR Premium (YTD)', value: '+$32.99', sub: 'YTD ADR', color: 'text-slate-300' },
      { label: 'RevPAR Premium (YTD)', value: '+$28.49', sub: 'YTD RevPAR', color: 'text-amber-600' },
    ],
    sections: [
      {
        title: 'Award & Recognition',
        icon: 'ri-trophy-line',
        iconBg: 'bg-amber-50',
        iconColor: 'text-amber-600',
        type: 'bullets',
        items: [
          { text: 'IHG Target Triumph Award — met all performance metrics while under active construction/renovation' },
          { text: 'Maintained market leadership in RevPAR and ADR throughout the 8-month renovation period' },
        ],
      },
      {
        title: 'Guest Satisfaction (8-Month Renovation Period)',
        icon: 'ri-star-line',
        iconBg: 'bg-rose-50',
        iconColor: 'text-rose-500',
        type: 'table',
        items: [
          { label: 'Overall Guest Experience Score', value: '86.67 avg' },
          { label: 'Room Cleanliness Score', value: '97.28 avg' },
          { label: 'Problem Handling Index', value: '87.37 avg' },
        ],
      },
      {
        title: 'Market Premium Performance',
        icon: 'ri-bar-chart-grouped-line',
        iconBg: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        type: 'table',
        items: [
          { label: 'YTD ADR Premium', value: '+$32.99 vs. market' },
          { label: 'YTD RevPAR Premium', value: '+$28.49 vs. market' },
          { label: 'Running 12-Month ADR Premium', value: '+$35.66 vs. market' },
          { label: 'Running 12-Month RevPAR Premium', value: '+$15.76 vs. market' },
        ],
      },
    ],
    closingNote: 'COIAA demonstrates that operational excellence doesn\'t pause for construction. By maintaining brand standards and a guest-first culture throughout renovation, the property achieved IHG recognition and sustained market leadership in both ADR and RevPAR.',
  },
];

const portfolioStats = [
  { value: '$120M+', label: 'Assets Under Management', icon: 'ri-funds-box-line' },
  { value: '6+', label: 'Owned & Managed Properties', icon: 'ri-building-2-line' },
  { value: '$18M+', label: 'Annual Hotel Revenue Managed', icon: 'ri-bar-chart-grouped-line' },
  { value: '4+', label: 'Florida Markets Operated In', icon: 'ri-map-pin-2-line' },
];

// ─── Hero ────────────────────────────────────────────────────────────────────
function PortfolioHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative h-[520px] md:h-[620px] flex items-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Aerial%20drone%20view%20of%20multiple%20Florida%20hotel%20properties%20along%20a%20coastline%2C%20luxury%20resort%20buildings%2C%20turquoise%20ocean%20water%2C%20palm%20trees%2C%20warm%20golden%20sunset%20light%2C%20cinematic%20wide%20angle%20photography%2C%20premium%20hospitality%20real%20estate%2C%20rich%20amber%20and%20teal%20tones%2C%20clear%20sky%2C%20beautiful%20Florida%20landscape&width=1920&height=900&seq=portfolio-hero-360hg&orientation=landscape"
          alt="360 Hospitality Group Portfolio"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/20"></div>
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pb-16 md:pb-20">
        <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-5">
            <div className="h-px w-8 bg-amber-400"></div>
            <span className="text-xs font-medium text-amber-400 uppercase tracking-[0.25em]">Our Portfolio</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-light text-white leading-tight tracking-tight mb-4">
            Properties We've<br />
            <span className="text-amber-400 italic">Transformed</span>
          </h1>
          <p className="text-lg text-gray-300 font-light max-w-2xl leading-relaxed">
            From underperforming independents to top-ranked branded properties — every engagement is built around measurable results.
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── Stats Bar ───────────────────────────────────────────────────────────────
function StatsBar() {
  const { ref, visible } = useVisible();
  return (
    <div ref={ref} className="bg-amber-500">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0">
          {portfolioStats.map((stat, i) => (
            <div
              key={i}
              className={`flex flex-col items-center text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${i < portfolioStats.length - 1 ? 'md:border-r md:border-amber-400/40' : ''}`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="w-8 h-8 flex items-center justify-center mb-2">
                <i className={`${stat.icon} text-xl text-white/70`}></i>
              </div>
              <p className="text-3xl md:text-4xl font-light text-white mb-1">{stat.value}</p>
              <p className="text-xs font-medium text-white/70 uppercase tracking-widest leading-tight">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Case Study Modal ─────────────────────────────────────────────────────────
function CaseStudyModal({ cs, onClose }: { cs: CaseStudyData; onClose: () => void }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-8">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto relative">

        {/* Header image */}
        <div className="relative h-52 rounded-t-2xl overflow-hidden">
          <img src={cs.image} alt={cs.fullName} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent"></div>
          <div className="absolute bottom-5 left-6 right-14">
            <div className="flex items-center space-x-2 mb-1">
              <span className="text-xs font-medium text-amber-400 uppercase tracking-widest">{cs.brandFamily}</span>
              <span className="text-white/40 text-xs">·</span>
              <span className="text-xs text-white/60 font-light">{cs.location}</span>
            </div>
            <h3 className="text-2xl font-light text-white leading-tight">{cs.fullName}</h3>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-black/40 rounded-full text-white hover:bg-black/60 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Awards strip */}
        <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 flex flex-wrap gap-2 items-center">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-trophy-fill text-amber-500 text-sm"></i>
          </div>
          {cs.awards.map((a, i) => (
            <span key={i} className="text-xs font-medium text-amber-700 bg-amber-100 px-3 py-1 rounded-full whitespace-nowrap">{a}</span>
          ))}
          <span className={`ml-auto text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${cs.statusColor}`}>{cs.status}</span>
        </div>

        {/* Hero stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-100">
          {cs.heroStats.map((s, i) => (
            <div key={i} className="bg-white py-5 px-4 text-center">
              <p className={`text-2xl font-semibold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-slate-600 font-medium mt-1 leading-tight">{s.sub}</p>
            </div>
          ))}
        </div>

        {/* Tagline */}
        <div className="px-8 pt-6 pb-2">
          <p className="text-sm text-slate-500 font-light leading-relaxed italic border-l-2 border-amber-400 pl-4">{cs.tagline}</p>
        </div>

        {/* Sections */}
        <div className="px-8 pb-6 space-y-7 mt-4">
          {cs.sections.map((section) => (
            <div key={section.title}>
              <div className="flex items-center space-x-2 mb-4">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${section.iconBg}`}>
                  <i className={`${section.icon} ${section.iconColor} text-base`}></i>
                </div>
                <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-widest">{section.title}</h4>
              </div>

              {section.type === 'bullets' && (
                <ul className="space-y-2.5">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0"></div>
                      <p className="text-sm text-slate-600 font-light leading-relaxed">{item.text}</p>
                    </li>
                  ))}
                </ul>
              )}

              {section.type === 'table' && (
                <div className="rounded-xl overflow-hidden border border-slate-100">
                  {section.items.map((item, i) => (
                    <div key={i} className={`flex items-center justify-between px-5 py-3 ${i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                      <span className="text-sm text-slate-500 font-light">{item.label}</span>
                      <span className="text-sm font-medium text-slate-900 text-right">{item.value}</span>
                    </div>
                  ))}
                </div>
              )}

              {section.type === 'progression' && (
                <div className="space-y-3">
                  {(() => {
                    const scores = section.items.map((s) => parseFloat(s.value || '0'));
                    const minScore = Math.min(...scores);
                    const maxScore = Math.max(...scores);
                    const range = maxScore - minScore || 1;
                    const colors = ['bg-slate-400', 'bg-amber-400', 'bg-emerald-500'];
                    return section.items.map((item, i) => {
                      const val = parseFloat(item.value || '0');
                      const pct = Math.round(((val - minScore) / range) * 60) + 35;
                      return (
                        <div key={i} className="flex items-center gap-4">
                          <span className="text-xs text-slate-500 font-light w-20 shrink-0">{item.label}</span>
                          <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${colors[i] ?? 'bg-amber-400'} transition-all duration-700`}
                              style={{ width: `${pct}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-slate-900 w-12 text-right shrink-0">{item.value}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          ))}

          {/* Closing note */}
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
            <p className="text-sm text-slate-600 font-light leading-relaxed">{cs.closingNote}</p>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="px-8 pb-8">
          <Link
            to="/contact"
            onClick={onClose}
            className="w-full flex items-center justify-center space-x-2 py-3.5 rounded-full bg-[#1a2b3c] hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 text-white text-sm font-medium tracking-wide transition-all whitespace-nowrap cursor-pointer"
          >
            <span>Get Results Like These — Talk to Our Team</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── Featured Case Studies Section ───────────────────────────────────────────
function FeaturedCaseStudies() {
  const { ref, visible } = useVisible();
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const activeCS = caseStudies.find((c) => c.id === activeModal) ?? null;

  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <div className={`mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-5">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-500 uppercase tracking-[0.25em]">Case Studies</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="text-4xl md:text-5xl font-light text-white leading-tight">
              Proof in the<br />
              <span className="text-amber-400 italic">Numbers</span>
            </h2>
            <p className="text-base text-gray-400 font-light max-w-sm leading-relaxed">
              Real performance data from active properties under 360 management — no projections, no estimates.
            </p>
          </div>
        </div>

        {/* Case Study Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {caseStudies.map((cs, i) => (
            <div
              key={cs.id}
              className={`group relative bg-slate-900 rounded-2xl overflow-hidden border border-slate-800 hover:border-amber-500/40 transition-all duration-700 cursor-default ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={cs.image}
                  alt={cs.fullName}
                  className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${cs.id === 'coifl' ? 'object-[center_30%]' : 'object-center'}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>

                {/* Award badge */}
                <div className="absolute top-4 left-4">
                  <div className="flex items-center space-x-1.5 bg-amber-500 px-3 py-1.5 rounded-full">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-trophy-fill text-white text-xs"></i>
                    </div>
                    <span className="text-white text-xs font-medium whitespace-nowrap">IHG Target Triumph</span>
                  </div>
                </div>

                {/* Brand badge */}
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs font-light">{cs.brand}</span>
                </div>

                {/* Status pill */}
                <div className="absolute bottom-4 right-5">
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${cs.statusColor}`}>{cs.status}</span>
                </div>

                {/* Hotel Name */}
                <div className="absolute bottom-4 left-5 right-20">
                  <h3 className="text-lg font-medium text-white leading-tight drop-shadow-lg">{cs.fullName}</h3>
                  <p className="text-xs text-white/70 mt-1">{cs.location}</p>
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-4 gap-px bg-slate-800/60 border-t border-slate-800">
                {cs.heroStats.map((s, si) => (
                  <div key={si} className="bg-slate-900 py-4 px-3 text-center">
                    <p className={`text-base font-semibold ${s.color}`}>{s.value}</p>
                    <p className="text-[10px] text-slate-400 font-light mt-1 leading-tight">{s.sub}</p>
                  </div>
                ))}
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-sm text-gray-400 font-light leading-relaxed mb-5">{cs.tagline}</p>

                {/* Awards list */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {cs.awards.map((a, ai) => (
                    <span key={ai} className="text-xs text-amber-600 bg-amber-500/10 border border-amber-500/20 px-3 py-1 rounded-full whitespace-nowrap">{a}</span>
                  ))}
                </div>

                {/* CTA */}
                <button
                  onClick={() => setActiveModal(cs.id)}
                  className="w-full flex items-center justify-center space-x-2 py-3 rounded-full border border-amber-500/40 text-amber-400 text-sm font-medium hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-chart-line text-base"></i>
                  <span>View Full Case Study</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {activeCS && (
        <CaseStudyModal cs={activeCS} onClose={() => setActiveModal(null)} />
      )}
    </section>
  );
}

// ─── Current Portfolio Grid ───────────────────────────────────────────────────
const currentPortfolio = [
  {
    id: 'hie-cocoa',
    name: 'Holiday Inn Express Cocoa',
    location: 'Cocoa, FL',
    brand: 'IHG' as const,
    image: 'https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/97c9b568-7e2a-4b4a-96b2-3e758d4908d7_2024-07-17.webp?v=275b997d2a719267af7b7d16f1e33a40',
  },
  {
    id: 'hie-cocoa-beach',
    name: 'Holiday Inn Express Cocoa Beach',
    location: 'Cocoa Beach, FL',
    brand: 'IHG' as const,
    image: 'https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/521edf7e-98e0-4d8a-af4b-751d038accb5_IMG_0521.jpg?v=0fde945454d003f9a341c084eae56c85',
  },
  {
    id: 'candlewood-melbourne',
    name: 'Candlewood Suites Melbourne',
    location: 'Melbourne, FL',
    brand: 'IHG' as const,
    image: 'https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/f7e811c8-6cd7-4b69-8946-877f28686494_unnamed-4.webp?v=0eff18de543c7ba34f95a11f57dd6388',
  },
  {
    id: 'fairfield-melbourne-beach',
    name: 'Fairfield Inn & Suites Melbourne Beach',
    location: 'Melbourne Beach, FL',
    brand: 'Marriott' as const,
    image: 'https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/2fcd586b-e24d-4534-bb81-de0f107bb2b9_Exterior-Day-Time-.jpg?v=7e2bd8ef277d8479ceeaaa768c810060',
  },
  {
    id: 'space-coast-convention',
    name: 'Space Coast Convention Center',
    location: 'Cocoa, FL',
    brand: 'Independent' as const,
    image: 'https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/ec21cd7f-56d9-4e1a-8adf-508b8a4d3d6b_WhatsApp-Image-2026-04-27-at-18.41.03.jpeg?v=701bbbd00a518ff87afb4c7204df43b0',
  },
  {
    id: 'atlantic-inn',
    name: 'Atlantic Inn',
    location: 'Indialantic, FL',
    brand: 'Independent' as const,
    image: 'https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/6a841707-14c3-407e-8365-8701a2ab8707_WhatsApp-Image-2026-04-21-at-19.07.29.jpeg?v=dad6f0a9e31b450d17df967775fd2820',
  },
];

const brandConfig: Record<string, { label: string; badgeBg: string }> = {
  IHG: { label: 'IHG Hotels & Resorts', badgeBg: 'bg-emerald-700' },
  Marriott: { label: 'Marriott International', badgeBg: 'bg-red-700' },
  Independent: { label: 'Independent Property', badgeBg: 'bg-slate-700' },
};

function CurrentPortfolioGrid() {
  const { ref, visible } = useVisible();

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-5">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-600 uppercase tracking-[0.25em]">Portfolio</span>
            <div className="h-px w-8 bg-amber-500"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-slate-900 leading-tight mb-4">
            Properties We&apos;re Proud <span className="text-amber-500 italic">to Manage</span>
          </h2>
          <p className="text-base text-slate-500 font-light max-w-xl mx-auto leading-relaxed">
            A curated portfolio of hospitality properties across Florida&apos;s Space Coast
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {currentPortfolio.map((property, i) => {
            const brand = brandConfig[property.brand];
            return (
              <div
                key={property.id}
                className={`group relative bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-amber-200 transition-all duration-500 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                {/* Image container */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={property.image}
                    alt={property.name}
                    className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${property.id === 'candlewood-melbourne' || property.id === 'fairfield-melbourne-beach' || property.id === 'hie-cocoa-beach' || property.id === 'atlantic-inn' || property.id === 'hie-cocoa' ? 'object-center' : 'object-top'}`}
                  />

                  {/* Dark overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  {/* Brand badge — top right corner */}
                  <div className="absolute top-3 right-3">
                    <span className={`${brand.badgeBg} text-white text-[10px] font-semibold px-2.5 py-1 rounded-lg tracking-wide whitespace-nowrap`}>
                      {property.brand}
                    </span>
                  </div>

                  {/* Location pin — bottom left, hides on hover */}
                  <div className="absolute bottom-3 left-3 opacity-100 group-hover:opacity-0 transition-opacity duration-300">
                    <div className="flex items-center space-x-1 bg-black/50 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-map-pin-2-fill text-amber-400 text-xs"></i>
                      </div>
                      <span className="font-light">{property.location}</span>
                    </div>
                  </div>
                </div>

                {/* Card body */}
                <div className="p-5 pb-6">
                  <h3 className="text-sm font-semibold text-slate-900 leading-snug mb-1.5 group-hover:text-amber-600 transition-colors duration-300">
                    {property.name}
                  </h3>
                  <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-map-pin-line text-amber-500 text-xs"></i>
                    </div>
                    <span className="text-xs text-slate-400 font-light">{property.location}</span>
                    <span className="text-slate-200 text-xs">·</span>
                    <span className="text-xs text-slate-400 font-light">{brand.label}</span>
                  </div>
                </div>

                {/* Bottom accent line */}
                <div className="h-0.5 w-0 group-hover:w-full bg-amber-400 transition-all duration-500"></div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA Banner */}
        <div className={`mt-16 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="bg-slate-950 rounded-2xl px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-lg font-light text-white leading-snug mb-1">
                Looking to add your property to our portfolio?
              </p>
              <p className="text-sm text-slate-400 font-light">Let&apos;s connect and explore what&apos;s possible together.</p>
            </div>
            <Link
              to="/contact"
              className="group flex items-center space-x-2 px-6 py-3 rounded-full bg-[#1a2b3c] hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 text-white text-sm font-medium tracking-wide transition-all duration-300 whitespace-nowrap shrink-0 cursor-pointer"
            >
              <span>Contact Us</span>
              <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-1"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Previously Operated & Sold ───────────────────────────────────────────────
const previouslyOperated = [
  {
    id: 'hi-port-st-lucie',
    name: 'Holiday Inn Port St. Lucie',
    location: 'Port St. Lucie, FL',
    address: '10120 S Federal Hwy, Port St. Lucie, FL 34952',
    brand: 'IHG' as const,
    image: 'https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/d2249d97-20d3-469c-947b-3a1b5df878d7_Holiday-Inn-St-Lucie.webp?v=68f7b072157acf0d38a89dc3334275cc',
  },
  {
    id: 'hie-clermont',
    name: 'Holiday Inn Express Clermont',
    location: 'Clermont, FL',
    address: '1810 S Hwy 27, Clermont, FL 34711',
    brand: 'IHG' as const,
    image: 'https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/9178c5ac-2725-4c60-a56d-bd3547bbc078_Holiday-inn-express-Clermont-FL.webp?v=25f4662f4b9f626b01327772a6b1599d',
  },
  {
    id: 'hi-venice',
    name: 'Holiday Inn Venice',
    location: 'Venice, FL',
    address: '380 Commercial Ct, Venice, FL 34292',
    brand: 'IHG' as const,
    image: 'https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/366f1f38-d58f-4561-84bb-ec296d4ef348_Holiday-inn-Venice-FL.webp?v=decd1075eae9adfc4b2db5fefbce72ff',
  },
  {
    id: 'fairfield-tallahassee',
    name: 'Fairfield Inn & Suites by Marriott Tallahassee',
    location: 'Tallahassee, FL',
    address: '3219 N Monroe St, Tallahassee, FL 32303',
    brand: 'Marriott' as const,
    image: 'https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/dc53369d-0e66-4714-b0a6-7a48966e3f45_Fairfield-inn-and-Suites-by-Marriott-Tallahassee-FL.webp?v=a328ffa27030566781bf8db34c1707bf',
  },
];

function PreviouslyOperatedSection() {
  const { ref, visible } = useVisible();

  return (
    <section ref={ref} className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-5">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-600 uppercase tracking-[0.25em]">Track Record</span>
            <div className="h-px w-8 bg-amber-500"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-slate-900 leading-tight mb-4">
            A Legacy of <span className="text-amber-500 italic">Successful Exits</span>
          </h2>
          <p className="text-base text-slate-500 font-light max-w-2xl mx-auto leading-relaxed">
            These properties reflect our proven ability to operate, stabilize, and successfully transition hotel assets — a testament to disciplined management and value creation at every stage of ownership.
          </p>
          {/* Trust badge row */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
            <div className="flex items-center space-x-2 bg-white border border-slate-200 rounded-full px-4 py-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-checkbox-circle-fill text-emerald-500 text-sm"></i>
              </div>
              <span className="text-xs text-slate-600 font-medium">Successfully Operated</span>
            </div>
            <div className="flex items-center space-x-2 bg-white border border-slate-200 rounded-full px-4 py-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-hand-coin-fill text-amber-500 text-sm"></i>
              </div>
              <span className="text-xs text-slate-600 font-medium">Value-Maximizing Exits</span>
            </div>
            <div className="flex items-center space-x-2 bg-white border border-slate-200 rounded-full px-4 py-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-building-2-fill text-slate-500 text-sm"></i>
              </div>
              <span className="text-xs text-slate-600 font-medium">Branded Hotel Assets</span>
            </div>
          </div>
        </div>

        {/* Grid — 4 tiles across */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7">
          {previouslyOperated.map((property, i) => {
            const brand = brandConfig[property.brand];
            return (
              <div
                key={property.id}
                className={`group relative bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-amber-200 transition-all duration-500 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                {/* Image container */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={property.image}
                    alt={property.name}
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                  />

                  {/* Dark overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  {/* Previously operated badge — top left */}
                  <div className="absolute top-3 left-3">
                    <div className="flex items-center space-x-1 bg-slate-800/80 backdrop-blur-sm text-white text-[10px] font-semibold px-2.5 py-1 rounded-lg tracking-wide whitespace-nowrap">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-check-line text-emerald-400 text-xs"></i>
                      </div>
                      <span>Prev. Operated &amp; Sold</span>
                    </div>
                  </div>

                  {/* Brand badge — top right */}
                  <div className="absolute top-3 right-3">
                    <span className={`${brand.badgeBg} text-white text-[10px] font-semibold px-2.5 py-1 rounded-lg tracking-wide whitespace-nowrap`}>
                      {property.brand}
                    </span>
                  </div>

                  {/* Location pin — bottom left, hides on hover */}
                  <div className="absolute bottom-3 left-3 opacity-100 group-hover:opacity-0 transition-opacity duration-300">
                    <div className="flex items-center space-x-1 bg-black/50 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-map-pin-2-fill text-amber-400 text-xs"></i>
                      </div>
                      <span className="font-light">{property.location}</span>
                    </div>
                  </div>
                </div>

                {/* Card body */}
                <div className="p-5 pb-6">
                  <h3 className="text-sm font-semibold text-slate-900 leading-snug mb-1.5 group-hover:text-amber-600 transition-colors duration-300">
                    {property.name}
                  </h3>
                  <p className="text-xs text-slate-400 font-light leading-relaxed mb-2">{property.address}</p>
                  <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-map-pin-line text-amber-500 text-xs"></i>
                    </div>
                    <span className="text-xs text-slate-400 font-light">{property.location}</span>
                    <span className="text-slate-200 text-xs">·</span>
                    <span className="text-xs text-slate-400 font-light">{brand.label}</span>
                  </div>
                </div>

                {/* Bottom accent line */}
                <div className="h-0.5 w-0 group-hover:w-full bg-amber-400 transition-all duration-500"></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Development Pipeline Section ────────────────────────────────────────────
function DevelopmentPipelineSection() {
  const { ref, visible } = useVisible();

  return (
    <section ref={ref} className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-5">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-600 uppercase tracking-[0.25em]">In Development</span>
            <div className="h-px w-8 bg-amber-500"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-slate-900 leading-tight mb-4">
            Building the Future of<br />
            <span className="text-amber-500 italic">Florida Hospitality</span>
          </h2>
          <p className="text-base text-slate-500 font-light max-w-2xl mx-auto leading-relaxed">
            Our active development pipeline reflects our commitment to growth — bringing new branded assets to high-demand Florida markets and expanding the 360 Hospitality footprint.
          </p>
        </div>

        {/* Main Development Card */}
        <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="relative rounded-3xl overflow-hidden border border-slate-100 bg-white">

            {/* Rendering Image */}
            <div className="relative h-[480px] md:h-[580px] w-full overflow-hidden">
              <img
                src="https://storage.readdy-site.link/project_files/390fbdda-ccd9-47f3-ab63-db1f61ff7592/fb613fde-77fd-4533-9a66-897b458e7d18_Screenshot-2026-04-27-at-5.24.49PM.png?v=d1f1a6d1c317379d79f8c79196cc7049"
                alt="Fairfield Inn & Suites by Marriott — Cocoa Village, FL — Development Rendering"
                className="w-full h-full object-cover object-[center_35%]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-slate-950/70 via-slate-950/20 to-transparent hidden md:block"></div>

              {/* Pipeline badge — top left */}
              <div className="absolute top-6 left-6 flex items-center gap-3">
                <div className="flex items-center space-x-2 bg-amber-500 text-white text-xs font-semibold px-4 py-2 rounded-full whitespace-nowrap">
                  <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
                  <span>Active Development</span>
                </div>
                <div className="flex items-center space-x-2 bg-black/50 backdrop-blur-sm text-white text-xs px-4 py-2 rounded-full whitespace-nowrap">
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-map-pin-2-fill text-amber-400 text-xs"></i>
                  </div>
                  <span className="font-light">Cocoa Village, FL</span>
                </div>
              </div>

              {/* Brand badge — top right */}
              <div className="absolute top-6 right-6">
                <span className="bg-red-700 text-white text-[10px] font-semibold px-3 py-1.5 rounded-lg tracking-wide whitespace-nowrap">Marriott</span>
              </div>

              {/* Content overlay — bottom left */}
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                <div className="max-w-xl">
                  <p className="text-amber-400 text-xs font-medium uppercase tracking-[0.25em] mb-3">Fairfield Inn &amp; Suites by Marriott · 105 Keys</p>
                  <h3 className="text-3xl md:text-4xl font-light text-white leading-tight mb-4">
                    Fairfield Inn &amp; Suites<br />
                    <span className="text-amber-400 italic">Cocoa Village, FL</span>
                  </h3>
                  <p className="text-sm text-gray-300 font-light leading-relaxed mb-6 max-w-md">
                    A modern, upscale select-service hotel designed to enhance the growing waterfront district of historic Cocoa Village — blending Marriott&apos;s trusted brand standards with lifestyle-driven amenities.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-500/20 border border-amber-500/30">
                        <i className="ri-building-4-line text-amber-400 text-sm"></i>
                      </div>
                      <span className="text-xs text-gray-300 font-light">105 Guest Rooms</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-500/20 border border-amber-500/30">
                        <i className="ri-cup-line text-amber-400 text-sm"></i>
                      </div>
                      <span className="text-xs text-gray-300 font-light">Ground-Floor Coffee Shop</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-500/20 border border-amber-500/30">
                        <i className="ri-windy-line text-amber-400 text-sm"></i>
                      </div>
                      <span className="text-xs text-gray-300 font-light">Rooftop Bar &amp; Lounge</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom info bar */}
            <div className="bg-slate-950 px-8 md:px-12 py-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0">
                <div className="flex items-start space-x-4 md:pr-8 md:border-r md:border-slate-800">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 shrink-0">
                    <i className="ri-hotel-line text-amber-400 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-white uppercase tracking-widest mb-1">Hotel Features</p>
                    <p className="text-xs text-slate-400 font-light leading-relaxed">105 guest rooms built to Marriott brand standards. Rooftop bar &amp; lounge with elevated guest experience. Ground-floor coffee shop activating street-level presence.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 md:px-8 md:border-r md:border-slate-800">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 shrink-0">
                    <i className="ri-map-2-line text-amber-400 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-white uppercase tracking-widest mb-1">Location Highlights</p>
                    <p className="text-xs text-slate-400 font-light leading-relaxed">Walkable access to Cocoa Village shops, dining, and entertainment. Close proximity to Port Canaveral and Cocoa Beach. Near major aerospace and defense employers along the Space Coast.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 md:pl-8">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-500/10 border border-emerald-500/20 shrink-0">
                    <i className="ri-rocket-line text-emerald-400 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-white uppercase tracking-widest mb-1">Development Status</p>
                    <p className="text-xs text-slate-400 font-light leading-relaxed">Currently in planning, design, and entitlement phases with ongoing coordination between the development team, city officials, and project consultants.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust signals row */}
        <div className={`mt-14 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center bg-slate-50 rounded-2xl px-6 py-8 border border-slate-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-amber-100 mx-auto mb-4">
                <i className="ri-funds-box-line text-amber-600 text-xl"></i>
              </div>
              <p className="text-xl font-light text-slate-900 mb-2">Vertically Integrated</p>
              <p className="text-xs text-slate-500 font-light leading-relaxed">We develop, operate, and manage — giving us full control over quality and performance from day one.</p>
            </div>
            <div className="text-center bg-slate-50 rounded-2xl px-6 py-8 border border-slate-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-amber-100 mx-auto mb-4">
                <i className="ri-award-line text-amber-600 text-xl"></i>
              </div>
              <p className="text-xl font-light text-slate-900 mb-2">Brand-Ready Build</p>
              <p className="text-xs text-slate-500 font-light leading-relaxed">Every new development is designed to meet and exceed leading brand standards from the ground up.</p>
            </div>
            <div className="text-center bg-slate-50 rounded-2xl px-6 py-8 border border-slate-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-amber-100 mx-auto mb-4">
                <i className="ri-map-2-line text-amber-600 text-xl"></i>
              </div>
              <p className="text-xl font-light text-slate-900 mb-2">Strategic Markets</p>
              <p className="text-xs text-slate-500 font-light leading-relaxed">Developments are carefully selected in high-demand Florida markets with proven hospitality demand drivers.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Portfolio CTA ────────────────────────────────────────────────────────────
function PortfolioCTA() {
  const { ref, visible } = useVisible();
  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-500 uppercase tracking-[0.25em]">Add Your Property</span>
            <div className="h-px w-8 bg-amber-500"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-white leading-tight mb-6">
            Your Property Could Be<br />
            <span className="text-amber-400 italic">Our Next Case Study</span>
          </h2>
          <p className="text-base text-gray-400 font-light leading-relaxed mb-10 max-w-xl mx-auto">
            Every property in our portfolio started with a free performance assessment. No commitment. Just a clear picture of what's possible.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="group px-6 py-3 rounded-full bg-[#1a2b3c] hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 text-white text-sm font-medium tracking-wide transition-all duration-300 whitespace-nowrap flex items-center space-x-2"
            >
              <span>Get a Free Assessment</span>
              <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-1"></i>
            </Link>
            <Link
              to="/performance"
              className="group px-6 py-3 rounded-full border border-white/30 text-white text-sm font-light tracking-wide hover:border-amber-400/60 hover:bg-white/5 transition-all duration-300 whitespace-nowrap flex items-center space-x-2"
            >
              <span>Our Performance Model</span>
              <i className="ri-arrow-right-line text-base text-amber-400 transition-transform group-hover:translate-x-1"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function PortfolioPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <PortfolioHero />
      <BrandLogosStrip />
      <FeaturedCaseStudies />
      <StatsBar />
      <CurrentPortfolioGrid />
      <PreviouslyOperatedSection />
      <DevelopmentPipelineSection />
      <PortfolioCTA />
      <Footer />
    </div>
  );
}